/**
 * Timeline Entry Modal
 *
 * Modal for adding or editing timeline entries for an event.
 */

import { useState, useEffect } from 'react';
import { createTimelineEntry, updateTimelineEntry, type TimelineEntry } from '../../api/events';
import { getErrorMessage } from '../../api/client';
import Modal from '../ui/Modal';
import { combineEventDateWithTime, zonedTimeInputValue } from '../../utils/dateFormatting';

interface TimelineEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventDate: string;
  entry: TimelineEntry | null;
  onSaved: () => void;
}

export default function TimelineEntryModal({
  isOpen,
  onClose,
  eventId,
  eventDate,
  entry,
  onSaved,
}: TimelineEntryModalProps) {
  const isEditing = !!entry;
  const [label, setLabel] = useState('');
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate fields when editing
  useEffect(() => {
    if (entry) {
      setLabel(entry.label);
      setTime(zonedTimeInputValue(entry.time));
      setDescription(entry.description || '');
    } else {
      setLabel('');
      setTime('');
      setDescription('');
    }
    setError(null);
  }, [entry, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      setError('Label is required');
      return;
    }
    if (!time) {
      setError('Time is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Combine event date with the entered time, in the org's zone
      const entryTimeIso = combineEventDateWithTime(eventDate, time);

      if (isEditing && entry) {
        await updateTimelineEntry(eventId, entry.id, {
          label: label.trim(),
          time: entryTimeIso,
          description: description.trim() || null,
        });
      } else {
        await createTimelineEntry(eventId, {
          label: label.trim(),
          time: entryTimeIso,
          description: description.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" labelledBy="timeline-entry-title">
      <form onSubmit={handleSubmit}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="timeline-entry-title" className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Timeline Entry' : 'Add Timeline Entry'}
          </h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="entryLabel" className="block text-sm font-medium text-gray-700 mb-1">
              Label *
            </label>
            <input
              id="entryLabel"
              type="text"
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="e.g., Crew Call, Rehearsal, Show Start"
            />
          </div>

          <div>
            <label htmlFor="entryTime" className="block text-sm font-medium text-gray-700 mb-1">
              Time *
            </label>
            <input
              id="entryTime"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
            />
          </div>

          <div>
            <label htmlFor="entryDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="entryDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="Additional details..."
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !label.trim() || !time}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
